import { ApplicationContext, ConfigurationsContext } from '../App';
import {
	PalletizerContext,
	RuntimeContext,
} from '../context/PalletizerContext';
import { LayoutContent } from './Layout';
import Button from './PendantButton';
import { TabOption } from './Sidebar';
import useWindowSize from './common/WindowSize';

import BellImage from './images/bell.svg';
import ExclamationImage from './images/exclamation-circle.svg';
import InfoImage from './images/info-circle.svg';
import { COLOR, useContainerSize, isWebDeployment } from './shared/shared';
import { MachineConfig, PalletConfigObj } from 'blackbox';
import {
	Application,
	PalletizerInformation,
	InformationType,
	formaInfoTimeStamp,
	Alert,
	StackQueue,
} from 'libapi';
import React, {
	ReactNode,
	useContext,
	useState,
	useEffect,
	ReactElement,
	useRef,
} from 'react';

const SYNC_MODE = true;

interface InformationLogProps {
	log: { info: PalletizerInformation; seen: boolean };
	openPdfViewer: (query: string) => void;
}

export function InformationLog({ log, openPdfViewer }: InformationLogProps) {
	// Regex to identify clickable keywords
	const clickableRegex = /(INFO|ERR|CERROR)(\d+)/g;

	const formatLogDate = (description: string) => {
		const parts = description.split(' - ');
		if (parts.length < 3) {
			// Fallback: return the original description if not enough parts
			return description;
		}

		const [rawDate, rawTime, ...rest] = parts;
		if (!rawDate || !rawTime) return description;

		const date = new Date(rawDate);
		if (isNaN(date.getTime())) return description;

		const month = date.toLocaleString('en-US', { month: 'short' });
		const day = date.getDate();

		const [hour, minute] = rawTime.split(':');
		if (!hour || !minute) return description;

		const remainingDescription = rest.join(' - ');
		return `${month} ${day} - ${hour}:${minute} - ${remainingDescription}`;
	};

	// Parse the Description to include clickable spans
	const renderDescription = () => {
		const formattedDescription = formatLogDate(log.info.Description);
		const parts = formattedDescription.split(clickableRegex);

		return parts.map((part, index) => {
			if (index % 3 === 0) {
				// Plain text
				return <span key={index}>{part}</span>;
			} else if (index % 3 === 1) {
				// Match group 1 (INFO, ERROR, CERROR)
				const keyword = part;
				const errorCode = parts[index + 1]; // Match group 2 (the code)
				const query = `${keyword}${errorCode}`; // Convert code to a search query

				return (
					<span
						key={index}
						className="clickable-keyword"
						style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
						onClick={() => openPdfViewer(query)}
					>
						{keyword}
						{errorCode}
					</span>
				);
			} else {
				return null; // Skip the error code as it is part of the clickable span
			}
		});
	};

	return (
		<>
			<div
				className={`rounded border flex-row margin-bottom padding ${
					log.info.Type === 'Error'
						? 'background-transparent-red'
						: 'background-silver'
				}`}
			>
				<div className="flex-col">
					<div className="flex-col flex-center align-start font-1"></div>
				</div>
				<div className="flex-start align-center">
					<span className="color-blue">
						{renderDescription()}
						{!log.seen && <span className="color-red"> *</span>}
					</span>
				</div>
			</div>
		</>
	);
}

export const useMachineConfig = function (app: Application) {
	const [lMachineConfig, lSetMachineConfig] = useState<
		undefined | MachineConfig
	>(void 0);

	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'machineConfig';

	// Function to load config from local storage or API
	const loadConfig = () => {
		if (IS_WEB_DEPLOYMENT) {
			const storedConfig = localStorage.getItem(localStorageKey);
			if (storedConfig) {
				lSetMachineConfig(JSON.parse(storedConfig));
			} else {
				app.api.Machine.get()
					.then(pConfig => {
						lSetMachineConfig(pConfig);
						localStorage.setItem(localStorageKey, JSON.stringify(pConfig));
					})
					.catch(console.error);
			}
		} else {
			app.api.Machine.get()
				.then(pConfig => {
					lSetMachineConfig(pConfig);
				})
				.catch(console.error);
		}
	};

	// Initial load of the config
	useEffect(() => {
		loadConfig();
	}, []);

	// Expose a refresh function to reload the config
	const refreshMachineConfig = () => {
		loadConfig();
	};

	return { lMachineConfig, refreshMachineConfig };
};

// Use this function for synchronous fetching.
export async function fetchMachineConfig(
	app: Application
): Promise<MachineConfig> {
	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'machineConfig';

	// Check local storage in web deployment
	if (IS_WEB_DEPLOYMENT) {
		const storedConfig = localStorage.getItem(localStorageKey);
		if (storedConfig) {
			// Return parsed config from local storage
			return JSON.parse(storedConfig);
		}
	}

	// If not found in local storage or not web deployment, fetch from the API
	try {
		const config = await app.api.Machine.get();

		// If web deployment, store the fetched config in local storage
		if (IS_WEB_DEPLOYMENT) {
			localStorage.setItem(localStorageKey, JSON.stringify(config));
		}

		// Return the fetched config
		return config;
	} catch (error) {
		console.error('Failed to fetch machine config:', error);
		throw error;
	}
}

export async function updateMachineConfig(
	newConfig: MachineConfig,
	refreshMachineConfig: () => void,
	app: Application
) {
	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'machineConfig';

	if (IS_WEB_DEPLOYMENT) {
		// Update the configuration in local storage
		localStorage.setItem(localStorageKey, JSON.stringify(newConfig));
		console.log('Updated config in local storage');
	} else {
		// Update the configuration on the server
		try {
			const res = await app.api.Machine.update(newConfig);
			console.log('Updated config on the server:', res);
		} catch (error) {
			console.error('Error updating config on the server:', error);
			throw error;
		}
	}

	// Refresh the machine config in context to ensure it's up-to-date
	refreshMachineConfig();
}

type CoreAppConfig = { [key: string]: any };
export const useCoreAppConfig = function (app: Application) {
	// const app = useContext(ApplicationContext) as Application;
	const [coreAppConfig, setCoreAppConfig] = useState<undefined | CoreAppConfig>(
		undefined
	);

	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'coreAppConfig';

	// Function to load config from local storage or API
	const loadConfig = () => {
		if (IS_WEB_DEPLOYMENT) {
			const storedConfig = localStorage.getItem(localStorageKey);
			if (storedConfig) {
				setCoreAppConfig(JSON.parse(storedConfig));
			} else {
				app.api.CoreAppConfig.get()
					.then(config => {
						setCoreAppConfig(config);
						localStorage.setItem(localStorageKey, JSON.stringify(config));
					})
					.catch(console.error);
			}
		} else {
			app.api.CoreAppConfig.get()
				.then(config => {
					setCoreAppConfig(config);
				})
				.catch(console.error);
		}
	};

	// Initial load of the config
	useEffect(() => {
		loadConfig();
	}, []);

	// Expose a refresh function to reload the config
	const refreshCoreAppConfig = () => {
		loadConfig();
	};

	return { coreAppConfig, refreshCoreAppConfig };
};

// Synchronous fetch for CoreAppConfig with local storage check

export async function fetchCoreAppConfig(
	app: Application
): Promise<CoreAppConfig> {
	console.log('fetch 2');
	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'coreAppConfig';

	console.log('Fetching core app config..', IS_WEB_DEPLOYMENT);

	// Check local storage in web deployment
	if (IS_WEB_DEPLOYMENT) {
		const storedConfig = localStorage.getItem(localStorageKey);
		if (storedConfig) {
			// Return parsed config from local storage
			return JSON.parse(storedConfig);
		}
	}

	// If not found in local storage or not web deployment, fetch from the API
	try {
		const config = await app.api.CoreAppConfig.get();

		// If web deployment, store the fetched config in local storage
		if (IS_WEB_DEPLOYMENT) {
			localStorage.setItem(localStorageKey, JSON.stringify(config));
		}

		// Return the fetched config
		return config;
	} catch (error) {
		console.error('Failed to fetch core app config:', error);
		throw error;
	}
}

// Function to update CoreAppConfig with local storage and server handling
export async function updateCoreAppConfig(
	newConfig: CoreAppConfig,
	refreshCoreAppConfig: () => void,
	app: Application
) {
	const IS_WEB_DEPLOYMENT = isWebDeployment();
	const localStorageKey = 'coreAppConfig';

	if (IS_WEB_DEPLOYMENT) {
		// Update the configuration in local storage
		localStorage.setItem(localStorageKey, JSON.stringify(newConfig));
		console.log('Updated core app config in local storage');
	} else {
		// Update the configuration on the server
		try {
			const res = await app.api.CoreAppConfig.update(newConfig);
			console.log('Updated core app config on the server:', res);
		} catch (error) {
			console.error('Error updating core app config on the server:', error);
			throw error;
		}
	}

	// Refresh the core app config in context to ensure it's up-to-date
	refreshCoreAppConfig();
}

export const useVRDFConfig = function () {
	const lApp = useContext(ApplicationContext) as Application;
	const [lVRDFConfig, lSetVRDFConfig] = useState<
		undefined | { [key: string]: any }
	>(void 0);
	useEffect(function () {
		lApp.api.VRDFConfig.get()
			.then(function (pConfig) {
				lSetVRDFConfig(pConfig);
			})
			.catch(console.error);
	}, []);
	return lVRDFConfig;
};

export const useCoreIOConfig = function () {
	const app = useContext(ApplicationContext) as Application;
    const [coreIOConfig, setCoreIOConfig] = useState<undefined | { [key: string]: any }>(undefined);

    const IS_WEB_DEPLOYMENT = isWebDeployment();
    const localStorageKey = 'IOConfig';

    // Function to load config from local storage or API
    const loadConfig = () => {
        if (IS_WEB_DEPLOYMENT) {
            const storedConfig = localStorage.getItem(localStorageKey);
            if (storedConfig) {
                setCoreIOConfig(JSON.parse(storedConfig));
            } else {
                app.api.CoreIOConfig.get()
                    .then(config => {
                        setCoreIOConfig(config);
                        localStorage.setItem(localStorageKey, JSON.stringify(config));
                    })
                    .catch(console.error);
            }
        } else {
            app.api.CoreIOConfig.get()
                .then(config => {
                    setCoreIOConfig(config);
                })
                .catch(console.error);
        }
    };

    // Initial load of the config
    useEffect(() => {
        loadConfig();
    }, []);

    return coreIOConfig;
};
// TODO Come back to this function after adding structs for config format.
export function getConfigValueFromKey(key: string, obj: any): any {
	// Search config object for key. Config keys must be unique,
	// (within the given config, not globally across all configs)
	// We use this because of the dynamic nature of the config to generate the UI, we don't
	// always know the full indexpath of the value we want.
	if (typeof obj !== 'object' || obj === null) {
		return undefined;
	}

	const lowercaseKey = key.toLowerCase();
	for (const k in obj) {
		if (k.toLowerCase() === lowercaseKey) {
			return obj[k];
		}

		if (typeof obj[k] === 'object') {
			const result = getConfigValueFromKey(lowercaseKey, obj[k]);
			if (!!result && 'v' in result) {
				return result.v;
			}
		}
	}

	return undefined;
}

export function getBarcodeStartModeEnabled() {
	const io_config = useCoreIOConfig();
	if (!io_config) return false;

	return (
		(getConfigValueFromKey('scanner_1', io_config) ||
			getConfigValueFromKey('scanner_2', io_config)) &&
		getConfigValueFromKey('scanner_mode', io_config) === 'start'
	);
}

interface DotProps {
	pSize: number;
	pColor: COLOR;
	pShadowColor?: COLOR;
	className?: string;
}
export function Dot({ pColor, pSize, className, pShadowColor }: DotProps) {
	const size = useWindowSize();
	const lDefaultStyle = {
		backgroundColor: pColor,
		height: pSize,
		width: pSize,
		minWidth: pSize,
		minHeight: pSize,
		borderRadius: '50%',
		display: 'inline-block',
		boxShadow: pShadowColor ? `0 0 2px 3px ${pShadowColor}` : '',
	};

	const [lDotWidth, lSetDotWidth] = useState<number>(pSize); // default value for 1920x1080
	const [lStyle, lSetStyle] = useState<any>(lDefaultStyle);
	useEffect(() => {
		if (!size.width) return;
		lSetDotWidth((size.width / 1920) * pSize);
	}, [size]);
	useEffect(() => {
		const lNew = { ...lDefaultStyle };
		const lNewSize = lDotWidth;
		lNew.height = lNewSize;
		lNew.width = lNewSize;
		lNew.minHeight = lNewSize;
		lNew.minWidth = lNewSize;
		lSetStyle(lNew);
	}, [lDotWidth]);

	return <div className={className} style={lStyle}></div>;
}

interface ScrollProps {
	className?: string;
	children: ReactElement | ReactNode;
	horizontal?: boolean;
	showScrollBar?: boolean;
}

export function Scroll({
	className,
	children,
	horizontal = false,
}: ScrollProps) {
	const lButtonsHeight = 0;
	const lScrollInterval = 200;
	const {
			ref: lContainerRef,
			rect: lContainerRect,
			internalRef: lInternalRef,
		} = useContainerSize<HTMLDivElement>(),
		[lSize, lSetSize] = useState<number | undefined>(void 0),
		lScrollRef = useRef<HTMLDivElement>(null);

	useEffect(
		function () {
			/* debounce for size adjustments */
			if (!lContainerRect?.[horizontal ? 'width' : 'height']) return;
			lSetSize(void 0);
			let lTimeout = window.setTimeout(function () {
				if (lContainerRect[horizontal ? 'width' : 'height'])
					lSetSize(lContainerRect[horizontal ? 'width' : 'height'] - lButtonsHeight);
			}, 40);
			return function () {
				clearTimeout(lTimeout);
			};
		},
		[lContainerRect]
	);

	useEffect(
		function () {
			if (!lInternalRef.current) return;
			if (!lScrollRef.current) return;
			let lLastPosition = 0;
			let lLastTime = 0;

			let lDeceleration = 0.01;
			let lTimer: any = void 0;
			let lLastSpeed = 0;

			const lHandleTouchStart = function (pEvent: TouchEvent) {
				lLastTime = new Date().getTime();
				lLastPosition = horizontal
					? pEvent.touches[0].screenX
					: pEvent.touches[0].screenY;
			};

			const lHandleTouchMove = function (pEvent: TouchEvent) {
				if (!lScrollRef.current) return;
				const lPosition = horizontal
					? pEvent.touches[0].screenX
					: pEvent.touches[0].screenY;
				const lTime = new Date().getTime();
				const lDelta = lPosition - lLastPosition;
				const lTimeDelta = lTime - lLastTime;
				const lSpeed = lDelta / lTimeDelta;
				horizontal
					? (lScrollRef.current.scrollLeft -= lDelta)
					: (lScrollRef.current.scrollTop -= lDelta);

				lLastPosition = lPosition;
				lLastTime = lTime;
				lLastSpeed = lSpeed;
			};

			const lHandleTouchEnd = function (pEvent: TouchEvent) {
				if (!lScrollRef.current) return;
				if (lTimer) {
					window.clearInterval(lTimer);
					lTimer = void 0;
				}
				const lScrollMaxPos =
					lScrollRef.current.scrollHeight -
					(horizontal
						? lScrollRef.current.clientWidth
						: lScrollRef.current.clientHeight);
				const lTimeIncrement = 3; /* ms */
				let lIsPositive = lLastSpeed >= 0;
				let lDecel = lIsPositive ? -1 * lDeceleration : lDeceleration;

				lTimer = window.setInterval(function () {
					if (!lScrollRef.current) {
						window.clearInterval(lTimer);
						lTimer = void 0;
						return;
					}

					horizontal
						? lScrollRef.current.scrollLeft
						: (lScrollRef.current.scrollTop -= lLastSpeed * lTimeIncrement);
					lLastSpeed += lDecel * lTimeIncrement;
					if (lLastSpeed * (lIsPositive ? 1 : -1) < 0) {
						window.clearInterval(lTimer);
						lTimer = void 0;
						return;
					}
					if (
						(horizontal
							? lScrollRef.current.scrollLeft
							: lScrollRef.current.scrollTop) <= 0 ||
						(horizontal
							? lScrollRef.current.scrollLeft
							: lScrollRef.current.scrollTop) >= lScrollMaxPos
					) {
						window.clearTimeout(lTimer);
						lTimer = void 0;
						return;
					}
				}, lTimeIncrement);
			};
			lInternalRef.current.addEventListener('touchstart', lHandleTouchStart);
			lInternalRef.current.addEventListener('touchend', lHandleTouchEnd);
			lInternalRef.current.addEventListener('touchmove', lHandleTouchMove);

			return function () {
				lInternalRef.current?.removeEventListener('touchstart', lHandleTouchStart);
				lInternalRef.current?.removeEventListener('touchend', lHandleTouchEnd);
				lInternalRef.current?.removeEventListener('touchmove', lHandleTouchMove);
			};
		},
		[lInternalRef, lScrollRef]
	);

	return (
		<div className="full-size flex-col flex-start" ref={lContainerRef}>
			<div
				className={className + ' no-scrollbar'}
				ref={lScrollRef}
				style={
					lSize
						? horizontal
							? {
									maxWidth: lSize,
									width: lSize,
									overflowX: 'scroll',
							  }
							: {
									maxHeight: lSize,
									height: lSize,
									overflowY: 'scroll',
							  }
						: {}
				}
			>
				{lSize !== void 0 && children}
			</div>
		</div>
	);
}

export interface GeneralProps {
	pTab: TabOption;
}

export default function General({ pTab }: GeneralProps) {
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;

	const { all_configs, set_all_configs } = useContext(
		ConfigurationsContext
	) as any;
	const app = useContext(ApplicationContext) as Application;

	const [currentAlert, setCurrentAlert] = useState<Alert | undefined>(void 0);
	const getNextAlert = function () {
		const lAlert = lRuntimeContext.alertQueue.peek();
		if (lAlert) setCurrentAlert(lAlert);
	};

	useEffect(
		function () {
			if (currentAlert) return;
			getNextAlert();
		},
		[lRuntimeContext]
	);
	useEffect(function () {
		if (!isWebDeployment()) {
			app.api.Pallet.get().then(set_all_configs).catch(console.error);
		}
	}, []);

	return (
		<LayoutContent
			pTab={{
				IconClass: pTab.IconClass,
				Name: 'General',
			}}
		>
			<div className="full-size"></div>
		</LayoutContent>
	);
}

function validArrayIndex(pArray: any[], pIndex: number) {
	return !(pIndex >= pArray.length || pIndex < 0);
}

function pruneSequence(
	pStackQueue: StackQueue,
	pPalletConfig: PalletConfigObj
): boolean {
	const lPallets = pPalletConfig.config.pallets;
	for (let pIndex = 0; pIndex < pStackQueue.length; pIndex++) {
		const pStackItem = pStackQueue[pIndex];
		if (!validArrayIndex(lPallets, pStackItem.PalletID)) return false;
		const lPallet = lPallets[pStackItem.PalletID];
		if (!validArrayIndex(lPallet.Stack, pStackItem.StackID)) return false;
		const lStack = lPallet.Stack[pStackItem.StackID];
		if (pStackItem.LayerIndex >= lStack.layers.length)
			pStackItem.LayerIndex = 0; /* reset starting layer */
		const lLayer = lStack.layers[pStackItem.LayerIndex];
		if (!validArrayIndex(lPallet.Layouts, lLayer.index))
			return false; /* should not occur */
		const lLayout = lPallet.Layouts[lLayer.index];
		if (!validArrayIndex(lLayout.boxPositions, pStackItem.BoxIndex))
			pStackItem.BoxIndex = 0;
	}
	return true;
}
